import { ChangeEvent, useCallback, useLayoutEffect, useMemo, useState } from 'react';

import { Dimensions, MIME_TYPE_IMAGE_JPEG, MIME_TYPE_IMAGE_WEBP, getObjectKeys } from '@vestico/utils';

import { ClientImageSourceSet } from '@vestico/model';

export interface useImageSourcesProps {
  url: string;
  srcSet?: ClientImageSourceSet;
  mediaAspectRatio?: number;
  didLoadThumb: boolean;
  containerDimensions: Dimensions;
}
export const useImageSources = ({ url, srcSet, mediaAspectRatio, didLoadThumb, containerDimensions }: useImageSourcesProps) => {
  const [disabledSourceSets, setDisabledSourceSets] = useState(0);

  useLayoutEffect(() => {
    setDisabledSourceSets(0);
  }, [url]);

  const onImageError = useCallback(
    (event: ChangeEvent<HTMLImageElement>) => {
      const loadedURL = event.currentTarget.src;
      const errorOnMainImage = loadedURL === url;
      if (errorOnMainImage) {
        setDisabledSourceSets(disabledSourceSets + 1);
      }
    },
    [disabledSourceSets, url]
  );
  const sources = useMemo(() => {
    const { width: containerWidth, height: containerHeight } = containerDimensions;
    if (!mediaAspectRatio || !didLoadThumb || !containerWidth || !srcSet) {
      return undefined;
    }

    const containerAspectRatio = containerWidth / containerHeight;
    const mediaIsWiderThanContainer = mediaAspectRatio > containerAspectRatio;
    const sizes = `${mediaIsWiderThanContainer ? containerHeight * mediaAspectRatio : containerWidth}px`;

    const sourceMarkups = [MIME_TYPE_IMAGE_WEBP, MIME_TYPE_IMAGE_JPEG].map((mimeType, index) => {
      const sources = srcSet[mimeType];
      if (!sources || disabledSourceSets > index) {
        return null;
      }
      const srcSetString = getObjectKeys(sources)
        .map((sizeKey) => {
          const size = typeof sizeKey === 'string' ? parseInt(sizeKey, 10) : sizeKey;
          const srcWidth = mediaAspectRatio < 1 ? mediaAspectRatio * size : size;
          const srcWidthString = Math.round(srcWidth).toFixed(0);
          return `${sources[sizeKey]} ${srcWidthString}w`;
        })
        .join(', ');

      if (srcSetString.length === 0) {
        return null;
      }
      return <source key={mimeType} sizes={sizes} srcSet={srcSetString} type={mimeType} />;
    });

    return sourceMarkups;
  }, [containerDimensions, mediaAspectRatio, didLoadThumb, srcSet, disabledSourceSets]);

  const result = useMemo(() => {
    return { sources, onImageError };
  }, [onImageError, sources]);
  return result;
};
